import { useState, useRef } from 'react'
import { Camera, Upload, Loader, X, Check, Crop } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { buildApiUrl, getApiHeaders } from '@/config/api'
import ImageCropModal from './ImageCropModal'

interface ProfilePictureUploadProps {
  currentAvatarUrl?: string
  onUploadSuccess?: (newAvatarUrl: string) => void
  onUploadError?: (error: string) => void
  size?: 'sm' | 'md' | 'lg'
}

export default function ProfilePictureUpload({ 
  currentAvatarUrl, 
  onUploadSuccess, 
  onUploadError,
  size = 'md' 
}: ProfilePictureUploadProps) {
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showCropModal, setShowCropModal] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-20 h-20', 
    lg: 'w-32 h-32'
  }

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        onUploadError?.('Please select an image file')
        return
      }

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024 // 10MB
      if (file.size > maxSize) {
        onUploadError?.('File size must be less than 10MB')
        return
      }

      // Show crop modal
      setSelectedFile(file)
      setShowCropModal(true)
    }
  }

  const handleCropComplete = (croppedFile: File) => {
    setSelectedFile(null)
    setShowCropModal(false)
    uploadProfilePicture(croppedFile)
  }

  const handleCropCancel = () => {
    setSelectedFile(null)
    setShowCropModal(false)
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const uploadProfilePicture = async (file: File) => {
    if (!file) return

    try {
      setUploading(true)
      setUploadProgress(0)

      console.log('🔄 Starting upload for file:', file.name, file.size, 'bytes')

      // Step 1: Get presigned URL
      console.log('📡 Step 1: Getting presigned URL...')
      
      // Direct fetch to bypass any apiClient issues
      const presignedResponse = await fetch(buildApiUrl('file-uploads/profile-picture/presigned-url'), {
        method: 'POST',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify({
          original_filename: file.name,
          file_size: file.size,
          mime_type: file.type
        })
      })
      
      if (!presignedResponse.ok) {
        throw new Error(`Presigned URL request failed: ${presignedResponse.status} ${presignedResponse.statusText}`)
      }
      
      const presignedData = await presignedResponse.json()

      console.log('📡 Presigned response:', presignedData)
      console.log('📡 Presigned data keys:', Object.keys(presignedData))
      console.log('📡 Presigned data success:', presignedData.success)
      console.log('📡 Presigned data upload_url:', presignedData.upload_url)
      console.log('📡 Presigned data fields:', presignedData.fields)
      console.log('📡 Presigned data s3_key:', presignedData.s3_key)

      if (!presignedData || !presignedData.success) {
        console.error('❌ Presigned response failed:', presignedData)
        throw new Error(presignedData?.error || 'Failed to get upload URL')
      }

      const { upload_url, fields, s3_key } = presignedData
      
      console.log('📤 Extracted values:')
      console.log('   upload_url:', upload_url)
      console.log('   fields:', fields)
      console.log('   s3_key:', s3_key)
      
      if (!upload_url || !fields || !s3_key) {
        throw new Error('Invalid presigned URL response')
      }

      console.log('✅ Got presigned URL:', upload_url)
      setUploadProgress(25)

      // Step 2: Upload to S3
      console.log('☁️ Step 2: Uploading to S3...')
      const formData = new FormData()
      
      // Add all fields from presigned URL
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string)
        console.log(`   Field: ${key} = ${value}`)
      })
      
      // Add the file
      formData.append('file', file)
      console.log('   Added file:', file.name)

      console.log('☁️ About to upload to S3 with URL:', upload_url)
      console.log('☁️ FormData entries:', [...formData.entries()].map(([k, v]) => [k, typeof v === 'object' ? 'File' : v]))

      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      })

      console.log('☁️ S3 response status:', uploadResponse.status)
      console.log('☁️ S3 response ok:', uploadResponse.ok)
      console.log('☁️ S3 response headers:', [...uploadResponse.headers.entries()])

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text()
        console.error('❌ S3 upload failed:', errorText)
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`)
      }

      const uploadResponseText = await uploadResponse.text()
      console.log('✅ S3 upload successful, response:', uploadResponseText)
      setUploadProgress(75)

      // Step 3: Confirm upload
      console.log('✅ Step 3: Confirming upload...')
      
      const confirmResponse = await fetch(buildApiUrl(`file-uploads/profile-picture/confirm-upload?s3_key=${encodeURIComponent(s3_key)}`), {
        method: 'POST',
        headers: getApiHeaders(),
        mode: 'cors',
        credentials: 'omit'
      })
      
      if (!confirmResponse.ok) {
        throw new Error(`Confirmation request failed: ${confirmResponse.status} ${confirmResponse.statusText}`)
      }
      
      const confirmData = await confirmResponse.json()
      console.log('✅ Confirm response:', confirmData)

      if (!confirmData || !confirmData.success) {
        throw new Error(confirmData?.error || 'Failed to confirm upload')
      }

      setUploadProgress(100)

      // Generate the final URL
      const newAvatarUrl = `https://hirehubble-dev-assets.s3.amazonaws.com/${s3_key}`
      console.log('🎉 Upload complete! URL:', newAvatarUrl)
      
      // Call success callback
      onUploadSuccess?.(newAvatarUrl)

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }

    } catch (error) {
      console.error('❌ Profile picture upload error:', error)
      onUploadError?.(error instanceof Error ? error.message : 'Upload failed')
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const triggerFileSelect = () => {
    fileInputRef.current?.click()
  }

  return (
    <>
      <div className="relative">
        <div className={`${sizeClasses[size]} bg-gray-300 rounded-full flex items-center justify-center overflow-hidden group cursor-pointer`} 
             onClick={triggerFileSelect}>
          {currentAvatarUrl ? (
            <img 
              src={currentAvatarUrl} 
              alt="Profile" 
              className={`${sizeClasses[size]} object-cover`} 
            />
          ) : (
            <Camera className={`${iconSizes[size]} text-gray-600`} />
          )}
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
            {uploading ? (
              <div className="text-center text-white">
                <Loader className={`${iconSizes[size]} animate-spin mx-auto`} />
                <div className="text-xs mt-1">{uploadProgress}%</div>
              </div>
            ) : (
              <div className="flex items-center space-x-1">
                <Crop className={`${iconSizes[size]} text-white`} />
                <Upload className={`${iconSizes[size]} text-white`} />
              </div>
            )}
          </div>
        </div>

        {/* Upload Button */}
        <button
          onClick={triggerFileSelect}
          disabled={uploading}
          className="absolute bottom-0 right-0 bg-blue-600 text-white rounded-full p-1 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Upload and crop profile picture"
        >
          {uploading ? (
            <Loader className="w-3 h-3 animate-spin" />
          ) : (
            <Crop className="w-3 h-3" />
          )}
        </button>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
      </div>

      {/* Crop Modal */}
      {selectedFile && (
        <ImageCropModal
          isOpen={showCropModal}
          imageFile={selectedFile}
          onClose={handleCropCancel}
          onCropComplete={handleCropComplete}
          aspectRatio={1} // Square aspect ratio for profile pictures
        />
      )}
    </>
  )
}
